import { isResource } from '@medplum/core'
import type {
  ExtendedResource,
  ExtendedResourceConstructor,
  ResourceOf,
} from '@seen/core/ExtendedResource'
import { invariant } from '@seen/core/invariant'
import { fhirSearchUrl } from '@seen/core/medplum'
import {
  getSearchKey,
  normalizeQuery,
  type QueryTypes,
} from '@seen/core/search'
import { skipToken, type UseQueryResult } from '@tanstack/react-query'
import { NO_QUERY } from '#src/constants/no-query.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'
import type { UseSearchOneOptions } from '#src/hooks/useSearchOne.ts'

/**
 * @param query - The query to search for.
 * @param instance - The instance to create if not found.
 * @param options - The options to pass to the query.
 * @returns The result of the query.
 * @deprecated Use useSearchOne instead.
 */
export function useSearchOneOrCreate<T extends ExtendedResource>(
  query: QueryTypes | null,
  instance: T | null,
  options: UseSearchOneOptions<T> = {}
): UseQueryResult<T | null> {
  const medplum = useMedplum()

  const parameters = normalizeQuery(query ? { ...query, _count: 1 } : undefined) // Even though medplum.searchOne automatically adds _count=1 (https://github.com/medplum/medplum/blob/df3fe74c9372c336ff7163591eed96aebf4306b5/packages/core/src/client.ts#L1523), we need to explicitly add it here to avoid caching issues (i.e. stomping over useSearch cache results with the same query). Also, this way if someone happens to call useSearch with _count=1, it'll hit the cache if useSearchOneOrCreate was called beforehand.

  const searchKey =
    instance?.fhirType && query
      ? getSearchKey(medplum, instance.fhirType, parameters)
      : NO_QUERY

  const resourceClass = instance?.constructor as ExtendedResourceConstructor<T>

  const result = useQuery({
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: cacheKeys.searchOneExtended(instance?.fhirType, searchKey),
    queryFn:
      searchKey === NO_QUERY
        ? skipToken
        : async () => {
            invariant(instance, 'Instance is required')

            const searchResult = await medplum.searchOneResourceClass(
              resourceClass,
              parameters,
              options.search
            )

            if (searchResult) {
              return searchResult
            }

            const { searchParams } = fhirSearchUrl(
              instance.fhirType,
              parameters,
              medplum
            )

            // Note: mepdlum.createResourceIfNoneExist errors if there are multiple matches, so we first use searchOne above. See https://www.hl7.org/fhir/http.html#cond-update
            const createdResource = await medplum.createResourceIfNoneExist(
              instance.toFhirResource(),
              searchParams.toString()
            )

            return isResource(createdResource)
              ? resourceClass.fromFhirResource(
                  createdResource as ResourceOf<typeof instance>
                )
              : null
          },
    ...options?.query,
    meta: {
      ...options?.query?.meta,
      queryParams: parameters,
      resourceClass,
    },
  })

  useErrorToast(
    result.error &&
      `Error accessing ${searchKey}: ${result.error.message ?? 'Unknown error'}`
  )

  return result
}
